import { useMemo, useState } from "react";
import BeachCard from "@/components/yts/BeachCard";
import BeachCompareTable from "@/components/yts/BeachCompareTable";
import BeachFinder from "@/components/yts/BeachFinder";
import CarouselRail from "@/components/yts/CarouselRail";
import ResponsiveRail from "@/components/yts/ResponsiveRail";
import SectionHead from "@/components/yts/SectionHead";
import { StaticSection } from "@/components/yts/HomeSections";
import { BEACH_CATEGORY, BEACH_GUIDE_INTRO, BEACH_REGIONS } from "@/data/beaches";
import { EMPTY_BEACH_FILTERS, filterBeaches, hasBeachFilters } from "@/data/beachFilters";
import { useExplore } from "@/lib/ExploreContext";
import { useDetailOverlay } from "@/lib/DetailOverlayContext";
import { useLang } from "@/lib/i18n";

/** The Beaches category: the finder on top, then the guide as region rails. */
export default function BeachCategoryView() {
  const { t } = useLang();
  const { beaches } = useExplore();
  const { openListing } = useDetailOverlay();
  const [filters, setFilters] = useState(EMPTY_BEACH_FILTERS);
  const matches = useMemo(() => filterBeaches(beaches, filters), [beaches, filters]);
  const searching = hasBeachFilters(filters);

  const card = (beach) => (
    <BeachCard key={beach.id} beach={beach} onOpen={() => openListing(beach.id)} />
  );

  // Regions come from the beaches themselves, in the order the guide runs them
  // — a region nobody has written a range for still gets its rail.
  const regions = useMemo(() => {
    const titles = Array.from(new Set(beaches.map((b) => b.region).filter(Boolean)));
    return titles.map((title) => ({
      title,
      range: BEACH_REGIONS.find((r) => r.title === title)?.range || "",
      beaches: beaches.filter((b) => b.region === title),
    }));
  }, [beaches]);

  return (
    <>
      <StaticSection id="beaches">
        <SectionHead
          title={BEACH_CATEGORY}
          subtitle={`${beaches.length} quiet beaches, coves and saltwater pools in and around metro San Juan.`}
        />
        <p className="text-[14px] leading-[1.6] text-[#6d6d6a] max-w-[720px]">{BEACH_GUIDE_INTRO}</p>
        <div className="mt-5">
          <BeachFinder
            filters={filters}
            onChange={setFilters}
            matchCount={matches.length}
            total={beaches.length}
          />
        </div>
      </StaticSection>

      {searching ? (
        <StaticSection>
          <SectionHead
            title={matches.length === 1 ? "1 beach match" : `${matches.length} beaches match`}
            subtitle="Tap a beach for its full guide."
          />
          {matches.length === 0 ? (
            <div className="text-center py-10 text-[14px] text-[#6d6d6a]">
              No beaches match that yet — try a broader search.
            </div>
          ) : (
            <ResponsiveRail>{matches.map((beach) => card(beach))}</ResponsiveRail>
          )}
        </StaticSection>
      ) : (
        regions.map((region) => (
          <CarouselRail key={region.title} title={region.title} subtitle={region.range}>
            {region.beaches.map((beach) => card(beach))}
          </CarouselRail>
        ))
      )}

      {matches.length > 0 && (
        <StaticSection className="pb-[100px]">
          <SectionHead title={t("beach.compare")} subtitle={t("beach.compareSub")} />
          <BeachCompareTable beaches={matches} />
        </StaticSection>
      )}
    </>
  );
}